import { useEffect, useRef } from "react";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  CatalogoDisparo,
  ContextoDisparo,
  OperadorFiltro,
  PropriedadeCatalogo,
  TipoPropriedade,
  filtroEstaCompleto,
  quantidadeValores,
  rotuloOperador,
  rotulosIntervalo,
} from "@/models/consignado/disparo.model";
import { FiltroDraft } from "./disparo-draft";
import { valoresParaOperador } from "./filtro-arvore";
import {
  MAX_OFFSET_RELATIVO,
  UNIDADES_RELATIVAS,
  UNIDADE_RELATIVA_LABEL,
  UnidadeRelativa,
  ehDataRelativa,
  montarDataRelativa,
  partesDataRelativa,
  previewDataRelativa,
} from "./data-relativa";
import { propriedade as buscarPropriedade, detalheDaCondicao, rotuloDoValor } from "./catalogo.helpers";
import { TIPO_ICON } from "./PropriedadeList";
import { useFiltroActive } from "./FiltroActiveContext";
import { useFiltroPopover } from "./FiltroPopoverContext";
import { brandOutline, brandSolid, fieldFont, labelStyles } from "./styleTokens";

type Props = {
  filtro: FiltroDraft;
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
  erro?: string;
  onUpdate: (id: string, patch: Partial<FiltroDraft>) => void;
  onRemove: (id: string) => void;
};

const inputType = (p?: PropriedadeCatalogo) =>
  p?.tipo === TipoPropriedade.DATA ? "date" : p?.tipo === TipoPropriedade.NUMERO ? "number" : "text";

/**
 * Valor de um campo de data: fixo ou relativo a agora.
 *
 * O relativo é o que faz uma rotina continuar correta amanhã — `AGORA-7d` é
 * resolvido na execução do disparo, enquanto uma data digitada fica congelada.
 * Por isso os dois modos ficam no mesmo campo, com a data resolvida logo
 * abaixo para quem cadastra conferir o que escolheu.
 */
function CampoValorData({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (valor: string) => void;
}) {
  const relativo = ehDataRelativa(valor);
  const partes = partesDataRelativa(valor) ?? { quantidade: 7, unidade: "d" as UnidadeRelativa };

  return (
    <VStack align="stretch" spacing={1.5}>
      <Select
        size="sm"
        {...fieldFont}
        value={relativo ? "relativa" : "fixa"}
        onChange={(e) =>
          onChange(e.target.value === "relativa" ? montarDataRelativa(7, "d") : "")
        }
      >
        <option value="fixa">Data fixa</option>
        <option value="relativa">Relativa a agora</option>
      </Select>

      {relativo ? (
        <>
          <Flex gap={1.5}>
            <Input
              size="sm"
              {...fieldFont}
              type="number"
              min={0}
              max={MAX_OFFSET_RELATIVO}
              value={partes.quantidade}
              onChange={(e) =>
                onChange(montarDataRelativa(Number(e.target.value || 0), partes.unidade))
              }
            />
            <Select
              size="sm"
              {...fieldFont}
              value={partes.unidade}
              onChange={(e) =>
                onChange(montarDataRelativa(partes.quantidade, e.target.value as UnidadeRelativa))
              }
            >
              {UNIDADES_RELATIVAS.map((u) => (
                <option key={u} value={u}>
                  {UNIDADE_RELATIVA_LABEL[u]} atrás
                </option>
              ))}
            </Select>
          </Flex>
          <Text fontSize="xs" color="gray.500" {...fieldFont}>
            {valor} — agora seria {previewDataRelativa(valor)}
          </Text>
        </>
      ) : (
        <Input
          size="sm"
          {...fieldFont}
          type="date"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </VStack>
  );
}


// Um filtro = uma pill (Tag), no estilo do "notebook" do Metabase. Sem campo
// escolhido, a pill fica em contorno — clicar nela registra o filtro como
// "alvo ativo" da sidebar (ver FiltroActiveContext), que mostra o column
// picker. Com campo já escolhido, a pill vira sólida e abre um popover só
// com operador/valores.
//
// A lista de filtros é uma conjunção AND — o backend não tem OU nem grupos,
// então não há combinador nem aninhamento aqui.
export default function FiltroCondicaoCard({
  filtro,
  contexto,
  catalogo,
  erro,
  onUpdate,
  onRemove,
}: Props) {
  const { openId, setOpenId, requestOpenId, erroId } = useFiltroPopover();
  const isOpen = openId === filtro.id;
  const onOpen = () => requestOpenId(filtro.id);
  const fecharComValidacao = () => requestOpenId(null);
  const { registerActiveFiltro, clearActiveFiltro } = useFiltroActive();

  const p = buscarPropriedade(catalogo, contexto, filtro.propriedade);
  const isConfigurado = !!filtro.propriedade;
  const operadores = p?.operadores ?? [];
  const esperado = quantidadeValores(filtro.operador);
  const listaLivre = esperado === null;
  const mostrarErro = (erroId === filtro.id && !filtroEstaCompleto(filtro)) || !!erro;

  // Assim que o campo é escolhido (ou trocado) pela sidebar, abre direto o
  // popover de operador/valores — sem isso o usuário precisaria de mais um
  // clique só pra ver o operador padrão.
  const campoAnteriorRef = useRef(filtro.propriedade);
  useEffect(() => {
    if (filtro.propriedade && filtro.propriedade !== campoAnteriorRef.current) {
      onOpen();
    }
    campoAnteriorRef.current = filtro.propriedade;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro.propriedade]);

  const ativarEscolhaDeCampo = () => {
    registerActiveFiltro(filtro.id, (nova) => {
      // Trocar de campo pode trocar os operadores permitidos — cai no
      // primeiro válido em vez de manter um que o backend recusaria.
      const operador = nova.operadores.includes(filtro.operador)
        ? filtro.operador
        : nova.operadores[0] ?? OperadorFiltro.IGUAL;
      onUpdate(filtro.id, {
        propriedade: nova.id,
        operador,
        valores: valoresParaOperador([], operador),
      });
    });
  };

  const handlePillClick = () => {
    if (isConfigurado) onOpen();
    else ativarEscolhaDeCampo();
  };

  const handleTrocarCampo = () => {
    ativarEscolhaDeCampo();
    // Fechamento sem validar: é o próprio filtro se corrigindo (reabre
    // sozinho quando o campo novo for escolhido), não uma troca pra outro.
    setOpenId(null);
  };

  const handleRemover = () => {
    clearActiveFiltro(filtro.id);
    if (isOpen) setOpenId(null);
    onRemove(filtro.id);
  };

  const setValor = (indice: number, valor: string) => {
    const valores = [...filtro.valores];
    valores[indice] = valor;
    onUpdate(filtro.id, { valores });
  };

  const handleOperador = (operador: OperadorFiltro) =>
    onUpdate(filtro.id, { operador, valores: valoresParaOperador(filtro.valores, operador) });

  const resumoValores = filtro.valores.filter((v) => v.trim() !== "");
  // Na pill o valor aparece em português: "AGORA-7d" e "PENDING_CONFIRMATION"
  // não dizem nada a quem lê a lista de filtros de relance. O valor cru
  // continua no draft.
  const rotulo = (v: string) => rotuloDoValor(contexto, filtro.propriedade, v);
  const detalhe = isConfigurado ? detalheDaCondicao(catalogo, contexto, filtro) : "";

  return (
    <Popover isOpen={isOpen} onClose={fecharComValidacao} placement="bottom-start" isLazy closeOnBlur={false}>
      <PopoverTrigger>
        <Tag
          size="lg"
          borderRadius="full"
          cursor="pointer"
          onClick={handlePillClick}
          {...(isConfigurado ? brandSolid("secondary") : brandOutline("secondary"))}
        >
          <TagLabel whiteSpace="nowrap" {...fieldFont}>
            {isConfigurado ? p?.descricao ?? filtro.propriedade : "Escolha o campo"}
            {detalhe && ` ${detalhe}`}
          </TagLabel>
          <TagCloseButton
            onClick={(e) => {
              e.stopPropagation();
              handleRemover();
            }}
          />
        </Tag>
      </PopoverTrigger>

      {isConfigurado && (
        <PopoverContent w="340px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody p={3}>
            <VStack align="stretch" spacing={3}>
              <Flex align="center" gap={1}>
                <IconButton
                  aria-label="Trocar campo"
                  icon={<ArrowBackIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={handleTrocarCampo}
                />
                {p && <Icon as={TIPO_ICON[p.tipo]} boxSize={3.5} color="gray.400" flexShrink={0} />}
                <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1} {...fieldFont}>
                  {p?.descricao ?? filtro.propriedade}
                </Text>
              </Flex>

              <FormControl>
                <FormLabel {...labelStyles} fontSize="xs">
                  Operador
                </FormLabel>
                <Select
                  size="sm"
                  {...fieldFont}
                  value={filtro.operador}
                  onChange={(e) => handleOperador(e.target.value as OperadorFiltro)}
                >
                  {operadores.map((op) => (
                    <option key={op} value={op}>
                      {rotuloOperador(op, p?.tipo)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* EM aceita de 1 a 30 valores — com lista fechada vira
                  seleção múltipla; sem lista, o valor é digitado. */}
              {listaLivre && p?.valores ? (
                <FormControl isInvalid={mostrarErro && resumoValores.length === 0}>
                  <FormLabel {...labelStyles} fontSize="xs">
                    Valores
                  </FormLabel>
                  <CheckboxGroup
                    value={resumoValores}
                    onChange={(v) => onUpdate(filtro.id, { valores: v as string[] })}
                  >
                    <Stack maxH="180px" overflowY="auto" spacing={1} pl={1}>
                      {p.valores.map((opcao) => (
                        <Checkbox key={opcao} value={opcao} size="sm" colorScheme="green" {...fieldFont}>
                          <Text fontSize="xs" title={opcao} {...fieldFont}>
                            {rotulo(opcao)}
                          </Text>
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                  <FormErrorMessage {...fieldFont}>Selecione ao menos um valor.</FormErrorMessage>
                </FormControl>
              ) : listaLivre ? (
                <FormControl isInvalid={mostrarErro && resumoValores.length === 0}>
                  <FormLabel {...labelStyles} fontSize="xs">
                    Valores (separados por vírgula)
                  </FormLabel>
                  <Input
                    size="sm"
                    {...fieldFont}
                    value={filtro.valores.join(", ")}
                    onChange={(e) =>
                      onUpdate(filtro.id, {
                        valores: e.target.value.split(",").map((v) => v.trim()),
                      })
                    }
                  />
                  <FormErrorMessage {...fieldFont}>Informe ao menos um valor.</FormErrorMessage>
                </FormControl>
              ) : (
                Array.from({ length: esperado ?? 1 }, (_, i) => i).map((i) => (
                  <FormControl key={i} isInvalid={mostrarErro && !(filtro.valores[i] ?? "").trim()}>
                    <FormLabel {...labelStyles} fontSize="xs">
                      {esperado === 2 ? rotulosIntervalo(p?.tipo)[i] : "Valor"}
                    </FormLabel>
                    {p?.valores ? (
                      <Select
                        size="sm"
                        {...fieldFont}
                        placeholder="Selecione"
                        value={filtro.valores[i] ?? ""}
                        onChange={(e) => setValor(i, e.target.value)}
                      >
                        {p.valores.map((opcao) => (
                          <option key={opcao} value={opcao}>
                            {rotulo(opcao)}
                          </option>
                        ))}
                      </Select>
                    ) : p?.tipo === TipoPropriedade.DATA ? (
                      <CampoValorData
                        valor={filtro.valores[i] ?? ""}
                        onChange={(v) => setValor(i, v)}
                      />
                    ) : (
                      <Input
                        size="sm"
                        {...fieldFont}
                        type={inputType(p)}
                        value={filtro.valores[i] ?? ""}
                        onChange={(e) => setValor(i, e.target.value)}
                      />
                    )}
                    <FormErrorMessage {...fieldFont}>Preencha o valor.</FormErrorMessage>
                  </FormControl>
                ))
              )}

              {mostrarErro && (
                <Flex align="center" gap={1.5} bg="red.50" borderRadius="md" px={2} py={1.5}>
                  <WarningTwoIcon boxSize={3} color="red.500" flexShrink={0} />
                  <Text fontSize="xs" color="red.600" {...fieldFont}>
                    {erro ?? "Preencha o filtro antes de fechar ou trocar de condição."}
                  </Text>
                </Flex>
              )}

              <Button size="sm" colorScheme="primary" alignSelf="flex-end" onClick={fecharComValidacao}>
                Concluir
              </Button>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      )}
    </Popover>
  );
}
